import { SVGProps, useEffect, useMemo } from 'react';

import { nanoid } from 'nanoid';

import { RowIndicatorVariant } from '../../../data-popper';
import { ChartElementTooltipConfig } from '../../_types';
import { useChartContext } from '../../ChartProvider';
import { useTooltipConfig } from '../../TooltipConfigProvider';

type Args<T> = {
  tooltipConfig: ChartElementTooltipConfig | undefined;
  indicatorVariant: RowIndicatorVariant | undefined;
  getStylesByPoint:
    | ((dataPoint: T, index: number) => SVGProps<SVGStopElement>)
    | undefined;
};

/**
 * Manages tooltip configuration of a chart element.
 * Returns element id which should be passed to `unit` prop of the chart element to correctly resolve config in the Tooltip.
 * Using `unit` as this one of a few props recharts allows to pass to the tooltip payload, and it has no built-in behavior.
 */
export const useChartElementTooltip = <T>({
  tooltipConfig,
  indicatorVariant,
  getStylesByPoint,
}: Args<T>): string => {
  const { setElementConfig } = useTooltipConfig<T>();
  const { data } = useChartContext<T>();
  const elementId = useMemo(() => nanoid(), []);

  useEffect(() => {
    setElementConfig(elementId, {
      indicatorVariant,
      tooltipConfig,
      getMarkerStylesByPoint:
        getStylesByPoint &&
        ((dataPoint: T) => {
          const index = data.indexOf(dataPoint);
          const styles = getStylesByPoint(dataPoint, index);
          return {
            color: styles.stopColor,
            opacity: styles.stopOpacity,
          };
        }),
    });
  }, [
    getStylesByPoint,
    indicatorVariant,
    tooltipConfig,
    data,
    elementId,
    setElementConfig,
  ]);

  return elementId;
};
